import React, { useState, useEffect } from 'react';
import { X, Check, Lock, Package } from 'lucide-react';
import { tenantsAPI } from '@/api/api';

// Fixed modules - always available
const FIXED_MODULES = [
  { code: 'dashboard', name: 'Dashboard', description: 'Main dashboard' },
  { code: 'profile', name: 'Profile Settings', description: 'User profile management' },
  { code: 'college-settings', name: 'College Settings', description: 'College configuration' },
  { code: 'consultants', name: 'Consultant', description: 'Consultant management' },
  { code: 'seat-booking', name: 'Seat Booking', description: 'Student seat booking' },
  { code: 'admission', name: 'Admission', description: 'Student admission management' },
  { code: 'fees', name: 'Fees Management', description: 'Fee collection and tracking' },
  { code: 'accounts', name: 'Accounts', description: 'Financial management' },
  { code: 'reports', name: 'Reports', description: 'Various reports' },
  { code: 'tickets', name: 'Ticket Management', description: 'Support tickets' }
];

// Configurable modules
const CONFIGURABLE_MODULES = [
  { code: 'attendance', name: 'Student Attendance', description: 'Attendance tracking' },
  { code: 'routine', name: 'Routine Management', description: 'Class routine/schedule' },
  { code: 'examination', name: 'Examination', description: 'Exam management' },
  { code: 'hr', name: 'HR Management', description: 'Staff HR management' },
  { code: 'leave', name: 'Leave Manager', description: 'Leave applications' },
  { code: 'library', name: 'Library Management', description: 'Library books and members' },
  { code: 'inventory', name: 'Inventory', description: 'Stock management' },
  { code: 'hostel', name: 'Hostel Management', description: 'Hostel and rooms' },
  { code: 'transport', name: 'Transport', description: 'Transport management' },
  { code: 'communication', name: 'Communication', description: 'Email/SMS/WhatsApp' },
  { code: 'transcripts', name: 'Transcripts', description: 'Document management' },
  { code: 'user-management', name: 'User Management', description: 'User accounts' },
  { code: 'roles', name: 'Role Management', description: 'Role permissions' },
  { code: 'lms', name: 'LMS (Learning)', description: 'Learning Management System - Course content, assignments' },
  { code: 'placement', name: 'Placement Cell', description: 'Company visits, job postings, placements' },
  { code: 'frontdesk', name: 'Front Desk', description: 'Visitor log, enquiries, postal, phone calls' }
];

const ModuleAccessModal = ({ isOpen, onClose, tenant, onUpdate }) => {
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planConfig, setPlanConfig] = useState(null);

  useEffect(() => {
    if (isOpen && tenant) {
      fetchTenantModules();
    }
  }, [isOpen, tenant]);

  const fetchTenantModules = async () => {
    setLoading(true);
    try {
      // Fetch tenant's current modules
      const response = await tenantsAPI.getTenantModules(tenant.id);
      const enabledModules = response.data.enabled_modules || [];
      
      // Only select configurable modules that are enabled
      const configurableEnabled = enabledModules.filter(m => 
        CONFIGURABLE_MODULES.some(cm => cm.code === m)
      );
      setSelectedModules(configurableEnabled);
      
      // Get plan config
      const plansResponse = await tenantsAPI.getSubscriptionPlans();
      const currentPlan = plansResponse.data.find(p => 
        p.code === tenant.subscription_plan || p.name === tenant.subscription_plan
      );
      setPlanConfig(currentPlan);
    } catch (error) {
      console.error('Error fetching tenant modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleCode) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleCode)) {
        return prev.filter(m => m !== moduleCode);
      } else {
        // Check if plan allows more modules
        const maxModules = planConfig?.max_configurable_modules ?? 0;
        if (maxModules !== -1 && prev.length >= maxModules) {
          alert(`Your plan (${tenant.subscription_plan}) allows maximum ${maxModules} configurable modules.`);
          return prev;
        }
        return [...prev, moduleCode];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Combine fixed + selected configurable modules
      const allModules = [
        ...FIXED_MODULES.map(m => m.code),
        ...selectedModules
      ];
      
      await tenantsAPI.updateTenantModules(tenant.id, {
        enabled_modules: allModules
      });
      
      alert('Module access updated successfully!');
      onUpdate && onUpdate();
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update modules');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const maxModules = planConfig?.max_configurable_modules ?? 0;
  const isUnlimited = maxModules === -1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-10 rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Package className="h-5 w-5" />
              Module Access Control
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              {tenant?.name} - Plan: {tenant?.subscription_plan?.toUpperCase()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading modules...</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Plan Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="font-medium text-gray-800 text-lg">
                    Configurable Modules: <span className="text-blue-600">{selectedModules.length}</span> / {isUnlimited ? '∞' : maxModules}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {isUnlimited 
                      ? '✨ Unlimited modules available with Premium plan' 
                      : maxModules === 0 
                        ? '⚠️ Upgrade to Standard or Premium to enable more modules'
                        : `📦 ${maxModules - selectedModules.length} more modules can be enabled`
                    }
                  </p>
                </div>
                {!isUnlimited && maxModules > 0 && (
                  <div className="text-right">
                    <div className="w-32 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 rounded-full transition-all"
                        style={{ width: `${(selectedModules.length / maxModules) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{selectedModules.length}/{maxModules} used</p>
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Modules */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-green-600" />
                Fixed Modules (Always Enabled)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {FIXED_MODULES.map(module => (
                  <div 
                    key={module.code}
                    className="p-2 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                      <span className="font-medium text-gray-800 text-sm truncate">{module.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Configurable Modules */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                📦 Configurable Modules
                <span className="text-sm font-normal text-gray-500">
                  (Click to enable/disable)
                </span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {CONFIGURABLE_MODULES.map(module => {
                  const isSelected = selectedModules.includes(module.code);
                  const isDisabled = !isSelected && !isUnlimited && maxModules !== -1 && selectedModules.length >= maxModules;
                  
                  return (
                    <button
                      key={module.code}
                      onClick={() => !isDisabled && toggleModule(module.code)}
                      disabled={isDisabled && !isSelected}
                      className={`p-2.5 border rounded-lg text-left transition-all ${
                        isSelected 
                          ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-500 shadow-sm' 
                          : isDisabled
                            ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                            : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className={`font-medium text-sm ${isSelected ? 'text-blue-800' : 'text-gray-800'}`}>
                          {module.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0 rounded-b-xl">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 text-base"
          >
            {saving ? 'Saving...' : '✓ Save Changes'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-medium transition-colors text-base"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleAccessModal;
